/**
 * @file getAPIVersion.ts
 * @description This module handles API versioning for the SDK. Merchants can specify
 * either 'legacy' or '2.4' as the API version. If no version is provided, a default version
 * will be used. This allows flexible control over API behavior while maintaining backward compatibility.
 */

/**
 * Enum for available API versions.
 * @readonly
 */
export const APIVersionTypes = {
  LEGACY: 'legacy',
  V_2_4: '2.4',
} as const;

/**
 * @description Defines the API version that the SDK should use.
 * - `"legacy"`: Uses the older API version with existing behaviors.
 * - `"2.4"`: Uses the new API version with updated functionalities.
 *
 * If this value is not set, the SDK will default to the predefined version,
 * ensuring backward compatibility.
 */
export type APIVersionOption = 'legacy' | '2.4';

/**
 * Type for API version.
 */
export type APIVersion = (typeof APIVersionTypes)[keyof typeof APIVersionTypes];

/**
 * Default API version to use when none is specified by the merchant.
 * Can be set to either 'legacy' or '2.4'.
 */
const defaultVersion: APIVersion = APIVersionTypes.LEGACY;

/**
 * Current API version set by the merchant or defaults to the defaultVersion.
 */
let currentAPIVersion: APIVersion = defaultVersion;

/**
 * Sets the API version for the SDK.
 * If an invalid version is provided, it defaults to the predefined defaultVersion.
 * In development mode, a warning is logged if no version is explicitly set.
 */
export function setAPIVersion(version: APIVersion | undefined): void {
  if (!version) {
    console.warn(
      `[Primer SDK] No API version was explicitly set. Defaulting to '${defaultVersion}'.`,
    );
  }
  currentAPIVersion = version ?? defaultVersion;
}

/**
 * Gets the current API version.
 */
export function getAPIVersion(): APIVersion {
  return currentAPIVersion;
}
